import { useEffect, useState } from "preact/hooks";
import { apiJson } from "../api";
import type { Analytics as A, Tenant } from "../api";

interface Props {
  tenant: Tenant;
}

export function Analytics({ tenant }: Props) {
  const [data, setData] = useState<A | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [range, setRange] = useState<"24h" | "7d" | "30d">("30d");

  useEffect(() => {
    let cancelled = false;
    const now = Date.now();
    const windows = { "24h": 86400e3, "7d": 7 * 86400e3, "30d": 30 * 86400e3 };
    const from = now - windows[range];
    apiJson<A>(
      `/api/tenants/${tenant.code}/analytics?from=${from}&to=${now}&limit=100`,
    )
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setErr(String(e)));
    return () => {
      cancelled = true;
    };
  }, [tenant.code, range]);

  return (
    <section>
      <header class="row">
        <h2>Analytics — {tenant.code}</h2>
        <select
          value={range}
          onChange={(e) =>
            setRange((e.target as HTMLSelectElement).value as typeof range)
          }
        >
          <option value="24h">Last 24h</option>
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
        </select>
      </header>

      {err && <div class="error">{err}</div>}
      {!data && !err && <p>Loading…</p>}
      {data && (
        <>
          <p>
            Total clicks: <strong>{data.totals.total ?? 0}</strong> — matched:{" "}
            <strong>{data.totals.matched ?? 0}</strong>
          </p>

          <div class="analytics-grid">
            <div>
              <h3>By country</h3>
              <table>
                <thead>
                  <tr>
                    <th>Country</th>
                    <th>Clicks</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_country.map((r) => (
                    <tr key={r.country}>
                      <td>{r.country ?? "—"}</td>
                      <td>{r.clicks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <h3>By keyword</h3>
              <table>
                <thead>
                  <tr>
                    <th>Keyword</th>
                    <th>Clicks</th>
                    <th>Matched</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_keyword.map((r) => (
                    <tr key={r.keyword}>
                      <td>
                        <code>{r.keyword}</code>
                      </td>
                      <td>{r.clicks}</td>
                      <td>{r.matched_clicks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
